<template>
  <v-dialog
    v-model="showModal"
    content-class="fixed-height-md draggable-modal"
    max-width="580px"
    eager
    :retain-focus="false"
    @keydown.esc="closeModal"
  >
    <modal-card
      ref="dragHandle"
      @close="closeModal"
      full
      fixed
      :title="modalTitle"
      class="answers-viewer"
      @click.native.stop.prevent
    >
      <div class="info-list ma-0" v-if="selectedQuestionnaire">
        <!-- Questionnaire Navigation -->
        <div class="info-list__item pb-5" v-if="hasMultipleAnswers">
          <v-btn
            v-for="record in sortedAnswers"
            :key="record.key"
            :color="getButtonColor(record.key)"
            text-color="white"
            class="mr-4"
            @click="selectQuestionnaire(record.key)"
          >
            {{ record.title }}
          </v-btn>
        </div>

        <!-- Questionnaire Fields -->
        <div
          class="info-list__item"
          v-for="schema in visibleQuestionnaireFields"
          :key="schema.field"
        >
          <div class="info-list__item-title" v-html="schema.title"></div>
          <div class="info-list__item-value">
            <template v-if="isArrayField(schema.field)">
              <v-chip
                v-for="(tag, idx) in getFieldValue(schema.field)"
                :key="idx"
                color="primary"
                text-color="white"
                label
              >
                {{ tag }}
              </v-chip>
            </template>
            <template v-else>
              {{ getFieldValue(schema.field) }}
            </template>
          </div>
        </div>
      </div>
    </modal-card>
  </v-dialog>
</template>

<script>
import { eventBus } from '@/lib/events';
import { mapGetters, mapState } from 'vuex';
import questionnaires from '../../data/questionnaries';

export default {
  name: 'QuestionnaireAnswersViewer',

  data() {
    return {
      record: null,
      selectedQuestionnaireKey: 'preScreenInterview',
      showModal: false,
      dragState: {
        initialized: false,
        isDragging: false,
        startX: 0,
        startY: 0,
        initialLeft: 0,
        initialTop: 0,
      },
      eventListeners: {
        mousedown: null,
        mousemove: null,
        mouseup: null,
      },
    };
  },

  computed: {
    ...mapState('rooms', ['roomInfo']),
    ...mapGetters('rooms', ['getParticipantRecords']),

    // Sorted answers for consistent display
    sortedAnswers() {
      return (this.record?.answers || []).slice().sort((a, b) => {
        return (b?.key || '').localeCompare(a?.key || '');
      });
    },

    // Check if we have multiple answers to show navigation
    hasMultipleAnswers() {
      return this.sortedAnswers.length > 1;
    },

    // Current selected questionnaire
    selectedQuestionnaire() {
      if (!this.record) return null;
      return this.record.answers.find(
        ({ key }) => key === this.selectedQuestionnaireKey
      );
    },

    // Selected answers data
    selectedAnswers() {
      return this.selectedQuestionnaire?.answer || {};
    },

    // Current questionnaire form configuration
    questionnaireForm() {
      if (!this.selectedQuestionnaire) return {};
      return questionnaires[this.selectedQuestionnaireKey] || {};
    },

    // Visible questionnaire fields based on office conditions
    visibleQuestionnaireFields() {
      const fieldsArr = Object.keys(this.questionnaireForm?.fields || {});

      return fieldsArr.reduce((acc, field) => {
        const schema = this.questionnaireForm.fields[field];

        if (this.isFieldVisible(schema)) {
          acc.push({
            ...schema,
            field,
          });
        }

        return acc;
      }, []);
    },

    // Modal title
    modalTitle() {
      if (!this.record || !this.selectedQuestionnaire) return '';
      return `${this.record.name}: ${this.selectedQuestionnaire.title}`;
    },
  },

  watch: {
    showModal(newVal) {
      if (!newVal) {
        this.resetModalPosition();
      }
    },
  },

  methods: {
    // Field visibility logic
    isFieldVisible(fieldSchema) {
      const { office } = fieldSchema;
      if (!office) return true;
      return (this.roomInfo?.on || '').startsWith(office);
    },

    // Button color logic
    getButtonColor(key) {
      return this.isSelected(key) ? 'appMain' : 'defaultActive';
    },

    // Selection state
    isSelected(key) {
      return this.selectedQuestionnaireKey === key;
    },

    // Field value getter with type checking
    getFieldValue(field) {
      return this.selectedAnswers[field] || '';
    },

    // Check if field is array type
    isArrayField(field) {
      return Array.isArray(this.selectedAnswers[field]);
    },

    // Questionnaire selection
    selectQuestionnaire(key) {
      this.selectedQuestionnaireKey = key;
    },

    // Show answer modal
    showAnswer(record) {
      this.record = record;
      this.showModal = true;
    },

    // Reset modal position
    resetModalPosition() {
      const el = this.$refs.dragHandle.$el || this.$refs.dragHandle;
      if (!el) return;

      el.style.position = 'relative';
      el.style.left = `0px`;
      el.style.top = `0px`;
    },

    // Clean up event listeners
    cleanupEventListeners() {
      const el = this.$refs.dragHandle.$el || this.$refs.dragHandle;
      if (!el) return;

      // Remove all stored event listeners
      Object.entries(this.eventListeners).forEach(([event, listener]) => {
        if (listener) {
          if (event === 'mousedown') {
            el.removeEventListener(event, listener);
          } else {
            document.removeEventListener(event, listener);
          }
          this.eventListeners[event] = null;
        }
      });

      const modalContainer = document.querySelector('.draggable-modal');
      if (modalContainer) {
        modalContainer.removeEventListener('click', this.closeModal);
      }

      this.dragState.initialized = false;
    },

    // Initialize drag functionality
    initDrag() {
      const el = this.$refs.dragHandle.$el;
      if (!el || this.dragState.initialized) return;

      this.dragState.initialized = true;

      const onMouseDown = (e) => {
        if (e.button !== 0) return;

        const rect = el.getBoundingClientRect();

        el.style.position = 'fixed';
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;

        this.dragState.isDragging = true;
        this.dragState.startX = e.clientX;
        this.dragState.startY = e.clientY;
        this.dragState.initialLeft = rect.left;
        this.dragState.initialTop = rect.top;

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      const onMouseMove = (e) => {
        if (!this.dragState.isDragging) return;

        const dx = e.clientX - this.dragState.startX;
        const dy = e.clientY - this.dragState.startY;

        let newLeft = this.dragState.initialLeft + dx;
        let newTop = this.dragState.initialTop + dy;

        // Modal and viewport sizes
        const modalWidth = el.offsetWidth;
        const modalHeight = el.offsetHeight;

        const minX = 0;
        const minY = 0;
        const maxX = window.innerWidth - modalWidth;
        const maxY = window.innerHeight - modalHeight;

        // Clamp to boundaries
        newLeft = Math.min(Math.max(newLeft, minX), maxX);
        newTop = Math.min(Math.max(newTop, minY), maxY);

        el.style.left = `${newLeft}px`;
        el.style.top = `${newTop}px`;
      };

      const onMouseUp = () => {
        this.dragState.isDragging = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      // Store references for cleanup
      this.eventListeners.mousedown = onMouseDown;
      this.eventListeners.mousemove = onMouseMove;
      this.eventListeners.mouseup = onMouseUp;

      el.addEventListener('mousedown', onMouseDown);

      const modalContainer = document.querySelector('.draggable-modal');
      if (modalContainer) {
        modalContainer.addEventListener('click', this.closeModal);
      }
    },

    // Close modal with cleanup
    closeModal() {
      this.showModal = false;
      this.cleanupEventListeners();
    },

    // Prepare and show answers
    async prepareAnswers(data) {
      const { participantId, key } = data;
      const record = this.getParticipantRecords(participantId);

      if (!record) return;

      this.showAnswer(record);

      // Set default key if not provided
      if (!key && this.sortedAnswers.length > 0) {
        this.selectQuestionnaire(this.sortedAnswers[0].key);
      } else if (key) {
        this.selectQuestionnaire(key);
      }

      await this.$nextTick();
      this.initDrag();
    },
  },

  mounted() {
    eventBus.on('showParticipantAnswers', this.prepareAnswers);
  },

  beforeDestroy() {
    this.cleanupEventListeners();
    eventBus.off('showParticipantAnswers', this.prepareAnswers);
  },
};
</script>

<style lang="less">
.draggable-modal {
  overflow: visible !important;
  box-shadow: none !important;
  pointer-events: auto !important;
}

.answers-viewer {
  width: 100%;
  max-width: 580px;
  height: 60vh;
  cursor: move;
  z-index: 1000;
  min-height: 0 !important;

  .info-list__item:first-child {
    padding-top: 20px;
  }

  .info-list__item:last-child {
    padding-bottom: 20px;
  }
}
</style>
